import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Modal,
  Card,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import type { ResolvedTemplate } from '@prooffleet/contracts';
import { ApiError, api, useSession } from '@prooffleet/client';
import { hasCapability } from '@prooffleet/contracts';
import { verdictColor } from '@prooffleet/ui';
import { EvidencePhoto } from '../components/EvidencePhoto.js';

interface HistoryPoint {
  inspectionId: string;
  at: string;
  value: number | null;
  verdict: string;
  note: string | null;
  photos: { objectKey: string; versionId: string | null }[];
}

interface HistoryPayload {
  itemKey: string;
  positionId: string | null;
  measurementKind: string | null;
  points: HistoryPoint[];
}

interface ChecklistPayload {
  unitNumber: string;
  resolved: ResolvedTemplate;
}

interface UnitPayload {
  _id: string;
  unitNumber: string;
  legalHold: {
    active: boolean;
    reason: string | null;
    caseReference: string | null;
    placedAt: string | null;
    heldObjectCount: number;
  };
}

/**
 * Карточка юнита с историей узла.
 *
 * Это единственное, чего нет ни у кого: ряд замеров по конкретному колесу
 * со снимками, подтверждающими каждое значение. Линия износа складывается
 * сама, без телеметрии и без обучения модели.
 */
function isoDay(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}

export function UnitCard(): React.JSX.Element {
  const { unitId = '' } = useParams();
  const [itemId, setItemId] = useState<string | null>(null);
  const [from, setFrom] = useState(isoDay(-30));
  const [to, setTo] = useState(isoDay(1));
  const [exporting, setExporting] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [caseRef, setCaseRef] = useState('');
  const queryClient = useQueryClient();
  const actor = useSession((state) => state.actor);

  /**
   * Скачивание в два шага: авторизованный запрос выдаёт одноразовый билет,
   * а сам поток забирается обычной навигацией. Иначе пришлось бы либо держать
   * сотни мегабайт в памяти вкладки, либо класть токен доступа в URL.
   */
  const exportPackage = async (): Promise<void> => {
    setExporting(true);
    try {
      const { ticket } = await api<{ ticket: string }>(
        `/units/${unitId}/evidence-package/ticket?from=${from}T00:00:00Z&to=${to}T00:00:00Z`,
        { method: 'POST' },
      );
      window.location.assign(`/api/evidence-package/${ticket}`);
    } finally {
      setExporting(false);
    }
  };

  const { data: checklist } = useQuery({
    queryKey: ['checklist', unitId],
    queryFn: () => api<ChecklistPayload>(`/units/${unitId}/checklist`),
  });

  const measurable = (checklist?.resolved.sections ?? [])
    .flatMap((section) => section.items)
    .filter((item) => item.type === 'measurement');

  const selected = itemId ?? measurable[0]?.id ?? null;

  const { data: units } = useQuery({
    queryKey: ['units'],
    queryFn: () => api<{ units: UnitPayload[] }>('/units'),
  });
  const unit = units?.units.find((u) => u._id === unitId);
  const onHold = unit?.legalHold.active ?? false;

  const canPlace = actor !== null && hasCapability(actor, 'legal_hold:place');
  const canAssignDriver = actor !== null && hasCapability(actor, 'defect:assign');

  const [driverOpen, setDriverOpen] = useState(false);
  const [driverId, setDriverId] = useState<string | null>(null);
  const [driverError, setDriverError] = useState<string | null>(null);

  const { data: people } = useQuery({
    queryKey: ['users'],
    enabled: canAssignDriver,
    queryFn: () =>
      api<{ members: { userId: string; displayName: string; roleKey: string }[] }>('/users'),
  });

  /**
   * Назначения не удаляются: новое закрывает предыдущее датой.
   * История назначений — доказательная база (кто отвечал за машину в день
   * происшествия), а не оперативное состояние.
   */
  const assignDriver = useMutation({
    mutationFn: () =>
      api('/assignments', {
        method: 'POST',
        body: { unitId, driverUserId: driverId, from: new Date().toISOString(), to: null },
      }),
    onSuccess: async () => {
      setDriverOpen(false);
      setDriverError(null);
      await queryClient.invalidateQueries({ queryKey: ['units'] });
    },
    onError: (err: unknown) => {
      setDriverError(err instanceof ApiError ? err.message : 'Could not assign the driver');
    },
  });
  const canRelease = actor !== null && hasCapability(actor, 'legal_hold:release');

  const holdMutation = useMutation({
    mutationFn: async () =>
      api(`/units/${unitId}/legal-hold${onHold ? '/release' : ''}`, {
        method: 'POST',
        body: onHold
          ? { reason: holdReason }
          : { reason: holdReason, caseReference: caseRef === '' ? null : caseRef },
      }),
    onSuccess: async () => {
      setHoldOpen(false);
      setHoldReason('');
      setCaseRef('');
      await queryClient.invalidateQueries({ queryKey: ['units'] });
    },
  });

  const { data: history } = useQuery({
    queryKey: ['history', unitId, selected],
    queryFn: () =>
      api<HistoryPayload>(`/units/${unitId}/history?itemId=${encodeURIComponent(selected ?? '')}`),
    enabled: selected !== null,
  });

  const points = history?.points ?? [];
  const trend =
    points.length >= 2 && points[0]?.value !== null && points.at(-1)?.value !== null
      ? (points.at(-1)!.value ?? 0) - (points[0]!.value ?? 0)
      : null;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="baseline">
        <div>
          <Title order={1}>{checklist?.unitNumber ?? 'Unit'}</Title>
          <Text className="pf-eyebrow" mt={2}>
            Unit record
          </Text>
        </div>
        {onHold && <Badge color="red">Legal hold</Badge>}
      </Group>

      {onHold && (
        <Alert color="red" variant="light" title="Data under legal hold">
          <Text fz="sm">{unit?.legalHold.reason}</Text>
          <Text fz="xs" c="dimmed" mt={4}>
            {unit?.legalHold.caseReference !== null && `Case ${unit?.legalHold.caseReference} · `}
            placed{' '}
            {unit === undefined || unit.legalHold.placedAt === null
              ? '—'
              : new Date(unit.legalHold.placedAt).toLocaleString()}
            {' · '}{unit?.legalHold.heldObjectCount} objects protected
          </Text>
        </Alert>
      )}

      <Select
        label="Component"
        placeholder="Pick a component"
        searchable
        data={measurable.map((item) => ({
          value: item.id,
          label: `${item.labels['en'] ?? item.itemKey}${item.positionLabel === null ? '' : ` — ${item.positionLabel}`}`,
        }))}
        value={selected}
        onChange={setItemId}
      />

      {selected !== null && points.length === 0 && (
        <Alert color="blue" variant="light">
          No readings for this component yet. The wear line appears after a few inspections.
        </Alert>
      )}

      {points.length > 0 && (
        <Card>
          <Group justify="space-between" mb="sm" align="baseline">
            <Text fw={600}>Reading history</Text>
            <Group gap="xs">
              <Text c="dimmed" fz="sm">
                {points.length} readings
              </Text>
              {trend !== null && (
                <Badge color={trend < 0 ? 'orange' : 'gray'}>
                  {trend > 0 ? '+' : ''}
                  {trend} over the period
                </Badge>
              )}
            </Group>
          </Group>

          <div className="pf-timeline">
            {points.map((point) => (
              <div key={point.inspectionId} className="pf-timeline-cell">
                <Stack gap={6}>
                  {point.photos[0] !== undefined ? (
                    <EvidencePhoto
                      objectKey={point.photos[0].objectKey}
                      versionId={point.photos[0].versionId}
                      height={92}
                      alt={history?.itemKey ?? ''}
                    />
                  ) : (
                    <Card h={92} p={0} bg="var(--mantine-color-default-hover)" />
                  )}

                  <Group justify="space-between" gap={4} wrap="nowrap">
                    <Text fw={700} className="pf-num">
                      {point.value ?? '—'}
                    </Text>
                    <Badge size="sm" color={verdictColor(point.verdict)}>
                      {point.verdict}
                    </Badge>
                  </Group>

                  <Text c="dimmed" fz="xs">
                    {new Date(point.at).toLocaleDateString()}
                  </Text>
                </Stack>
              </div>
            ))}
          </div>
        </Card>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Card>
          <Text fw={600} mb="xs">
            Evidence package
          </Text>
          <Text c="dimmed" fz="sm" mb="sm">
            A ZIP with the original photos, a hash manifest, a report, and instructions for
            independent verification. Every export is recorded in the chain of custody.
          </Text>
          <Group align="flex-end" gap="sm">
            <TextInput
              label="From"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.currentTarget.value)}
            />
            <TextInput
              label="To"
              type="date"
              value={to}
              onChange={(e) => setTo(e.currentTarget.value)}
            />
            <Button loading={exporting} onClick={() => void exportPackage()}>
              Export
            </Button>
          </Group>
        </Card>

        <Card>
          <Text fw={600} mb="xs">
            Legal hold
          </Text>
          <Text c="dimmed" fz="sm" mb="sm">
            While the hold is active, the retention policy leaves this unit alone and its
            inspection attachments are WORM-protected in storage. Placing and releasing are
            separate permissions: the person a case concerns is not the one who lifts it.
          </Text>
          {onHold ? (
            <Button
              color="red"
              variant="light"
              disabled={!canRelease}
              onClick={() => setHoldOpen(true)}
            >
              {canRelease ? 'Release hold' : 'Releasing requires owner permissions'}
            </Button>
          ) : (
            <Button color="red" disabled={!canPlace} onClick={() => setHoldOpen(true)}>
              Place hold
            </Button>
          )}
        </Card>

        <Card>
          <Text fw={600} mb="xs">
            Assigned driver
          </Text>
          <Text c="dimmed" fz="sm" mb="xs">
            The driver on record at the time of an inspection is part of the evidence. Assignments
            are never deleted — a new one closes the previous by date.
          </Text>
          <Button
            size="compact-xs"
            variant="default"
            disabled={!canAssignDriver}
            onClick={() => {
              setDriverId(null);
              setDriverError(null);
              setDriverOpen(true);
            }}
          >
            Assign driver
          </Button>
        </Card>

        <Card>
          <Text fw={600} mb={4}>
            Configuration
          </Text>
          <Text c="dimmed" fz="sm">
            Checklist items: {checklist?.resolved.itemCount ?? '—'}
          </Text>
          <Text c="dimmed" fz="sm">
            Template: {checklist?.resolved.templateKey ?? '—'} · revision{' '}
            {checklist?.resolved.templateVersion ?? '—'}
          </Text>
        </Card>
      </SimpleGrid>

      <Modal
        opened={holdOpen}
        onClose={() => setHoldOpen(false)}
        title={onHold ? 'Release legal hold' : 'Place legal hold'}
      >
        <Stack gap="md">
          <Textarea
            label="Reason"
            description="Stays in the audit log permanently"
            autosize
            minRows={3}
            value={holdReason}
            onChange={(e) => setHoldReason(e.currentTarget.value)}
          />
          {!onHold && (
            <TextInput
              label="Case reference"
              placeholder="CASE-2026-118"
              value={caseRef}
              onChange={(e) => setCaseRef(e.currentTarget.value)}
            />
          )}
          <Button
            color="red"
            disabled={holdReason.trim().length < 10}
            loading={holdMutation.isPending}
            onClick={() => holdMutation.mutate()}
          >
            {onHold ? 'Release' : 'Place'}
          </Button>
        </Stack>
      </Modal>

      <Modal
        opened={driverOpen}
        onClose={() => setDriverOpen(false)}
        title="Assign a driver to this unit"
      >
        <Stack gap="md">
          <Text c="dimmed" fz="sm">
            Assignments are history, not settings. The previous one is closed by date and stays on
            the record.
          </Text>
          <Select
            label="Driver"
            searchable
            data={(people?.members ?? [])
              .filter((m) => m.roleKey === 'driver')
              .map((m) => ({ value: m.userId, label: m.displayName }))}
            value={driverId}
            onChange={setDriverId}
          />
          {driverError !== null && <Alert color="red">{driverError}</Alert>}
          <Button
            loading={assignDriver.isPending}
            disabled={driverId === null}
            onClick={() => assignDriver.mutate()}
          >
            Assign
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
